/**
 * EmptyState — warm centered icon-tile + title + subtitle placeholder.
 * Used for honest empty and error states on all screens.
 * Design: warm icon tile (52×52, r:16, mist bg), title1 serif headline,
 * subhead secondary label. Matches the prototype's card/inset tone.
 * Atom purity: imports only react + tokens.
 */
import { CSSProperties, ReactNode } from 'react';
import { P, S, T } from '../../styles/tokens';

export interface EmptyStateProps {
  /** Optional leading icon — pass e.g. <Ic name="warning" size={28} color={P.muted} />. */
  icon?: ReactNode;
  /** Primary message (rendered as serif title2). */
  title: string;
  /** Optional supporting text below the title. */
  subtitle?: string;
}

export function EmptyState({ icon, title, subtitle }: EmptyStateProps) {
  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '56px 32px 0',
    boxSizing: 'border-box',
  };

  // Warm mist tile — 52×52, rounded, muted background
  const iconTileStyle: CSSProperties = {
    width: 52,
    height: 52,
    borderRadius: 16,
    background: P.mist,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: S.secondaryLabel,
    marginBottom: 20,
    flexShrink: 0,
  };

  const titleStyle: CSSProperties = {
    // title2 = serif bold 22px — prominent but not oversized for empty states
    font: T.title2,
    color: S.label,
    marginBottom: subtitle ? 8 : 0,
    letterSpacing: -0.2,
  };

  const subtitleStyle: CSSProperties = {
    font: T.subhead,
    color: S.secondaryLabel,
    maxWidth: 260,
    lineHeight: 1.5,
  };

  return (
    <div style={containerStyle}>
      {icon !== undefined && <div style={iconTileStyle}>{icon}</div>}
      <div style={titleStyle}>{title}</div>
      {subtitle !== undefined && <div style={subtitleStyle}>{subtitle}</div>}
    </div>
  );
}
